import {useEffect} from 'react'
import {useLocation} from 'react-router-dom'
import {useIsFetching} from '@tanstack/react-query'

/**
 * performHashScroll
 *
 * Attempts to smoothly scroll the page to the element with the given ID,
 * applying an automatic offset based on the height of:
 * - The main header (`header#header`)
 * - An optional top banner (`#headerBanner`)
 *
 * The scroll executes only if:
 * - The target element exists in the DOM
 * - The element is not locked using `data-hash-scroll-lock="true"`
 *
 * The function returns:
 * - true  → Scroll was performed successfully
 * - false → No scroll occurred (element missing or locked)
 *
 * @param {string} targetId - The ID of the DOM element to scroll to (without the leading #)
 * @returns {boolean} Whether scrolling was performed
 */
const performHashScroll = async (targetId) => {
    const element = document.getElementById(targetId)
    if (!element) return false

    // If data-hash-scroll-lock is active, abort
    if (element.getAttribute('data-hash-scroll-lock') === 'true') {
        return false
    }

    // Wait for 2 animation frames (layout stabilization)
    // it's important for the cases:
    // - PD page load / fast hash link click
    // - PD page with hash url load
    // It's still some risky
    await new Promise((resolve) => requestAnimationFrame(resolve))
    await new Promise((resolve) => requestAnimationFrame(resolve))

    // Dynamic offset: header + banner height
    const headerElement = document.querySelector('header#header')
    const headerHeight = headerElement ? headerElement.offsetHeight : 0

    const bannerElement = document.getElementById('headerBanner')
    const bannerHeight = bannerElement ? bannerElement.offsetHeight : 0

    const totalOffset = headerHeight + bannerHeight

    const elementPosition = element.getBoundingClientRect().top + window.scrollY
    const offsetPosition = elementPosition - totalOffset

    window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
    })

    return true
}

/**
 * useHashScroll
 *
 * A universal hash-based scrolling hook that works reliably in all scenarios,
 * including dynamically rendered Page Designer content.
 *
 * This hook handles 3 main cases:
 *
 * Case 1: Same‑page hash navigation
 *    - The user is already on the current page and clicks a link pointing to
 *      "#some-id" on the same page.
 *    - The hook attempts to scroll immediately since the element likely already exists.
 *
 * Case 2: Navigation from another page with a hash
 *    - Example: clicking a footer link on the home page that navigates to
 *      "/faq#shipping".
 *    - React Router changes the route without a full reload, and dynamic Page Designer
 *      content renders after navigation.
 *    - The hook detects the hash and waits for the target element to be created,
 *      then performs the scroll when available.
 *
 * Case 3: Page load or reload with a hash
 *    - The user opens a URL like "/faq#returns" directly or reloads the page.
 *    - The browser automatically attempts a native hash-scroll **before** React
 *      renders any Page Designer content (this is a normal browser behavior).
 *      This results in a race condition where the scroll happens too early and
 *      fails because the target element does not exist yet.
 *    - The DOM may not be fully rendered yet (especially with Page Designer blocks),
 *      so immediate scrolling may fail.
 *    - A MutationObserver watches for the target element to appear and scrolls
 *      once it's rendered.
 *
 * Note on `location.key`:
 * React Router generates a new `key` for every navigation triggered through
 * `<Link>` or `navigate()`, even if the path and hash stay the same.
 * Adding `key` ensures the scroll logic re-runs when clicking a React Router
 * link to the same hash (e.g. clicking "/faq#delivery" multiple times).
 * Native <a href> links do not change `key`.
 *
 * How it works:
 * - First attempts immediate scroll (covers case #1).
 * - If the element isn't found, sets up a MutationObserver to detect when
 *   dynamic content appears (covers cases #2 and #3).
 * - Automatically disconnects after successful scroll or when the effect cleans up.
 *
 */
export const useHashScroll = () => {
    const {hash, pathname, key} = useLocation()
    const isFetching = useIsFetching()

    useEffect(() => {
        const targetId = hash.replace('#', '')
        if (!targetId) return

        // Wait until all react query activity finishes
        if (isFetching > 0) return

        // Case 1 (same-page hash navigation): Try immediately
        performHashScroll(targetId).then((ok) => {
            if (ok) return
        })

        // Case 2 & 3: observe DOM changes until element exists and unlocks
        const observer = new MutationObserver((_, obs) => {
            const element = document.getElementById(targetId)

            // Still waiting for content
            if (!element) return

            // Still waiting until target block unlocks, example: accordion is opened
            const lock = element.getAttribute('data-hash-scroll-lock')
            if (lock === 'true') return

            performHashScroll(targetId)

            // disconnect when element exists, no lock and has scroll done
            obs.disconnect()
        })

        observer.observe(document.body, {
            childList: true,
            subtree: true
        })

        return () => {
            observer.disconnect()
        }
    }, [hash, pathname, key, isFetching])
}

/**
 * ScrollToHash
 *
 * A global component that activates the `useHashScroll` hook throughout
 * the entire application. It renders nothing visually and serves only
 * as a scroll-behavior controller for hash-based navigation.
 *
 * Why this component exists:
 * - It centralizes hash scrolling logic so you don't need to call
 *   `useHashScroll` inside every individual page or component.
 * - Ensures consistent behavior for:
 *   - Same-page hash navigation
 *   - Cross-page navigation with hashes (e.g., "/faq#returns")
 *   - Page reloads or direct deep links with a hash
 *   - Delayed rendering of Page Designer content or dynamic blocks
 *
 * @component
 */
const ScrollToHash = () => {
    useHashScroll()
    return null
}
export default ScrollToHash
